import {
  EMAIL_ROUTING_KINDS,
  EMAIL_ROUTING_TOKENS,
  EmailRoutingExtra,
  EmailRoutingKind,
  EmailRoutingKindInfo,
  EmailRoutingRule,
  EmailRoutingRules,
  EmailRoutingToken,
  VendorEmailSettings,
} from "../../../core/models/entities/VendorEmailSettings";
import { PutVendorEmailSettingsBody } from "../bodies/PutVendorEmailSettingsBody";

// Tolerant reading of the email-settings answer (see VendorEmailSettingsDto -
// the camelCase form of the API's VendorEmailSettingsDto.cs: one `kinds[]`
// entry per mail type with its stored `rule`, today's `defaultTo`, its
// `allowedTokens` and `allowsExtraRecipients`):
// - EVERY kind the API lists is kept, in the API's order, with its `name`,
//   `requiresTo` and `preferenceKey` (kinds[] is the contract: a kind the
//   screen has no texts for - e.g. the receipt of the dealer's OWN orders -
//   is shown by its API name and sent back like the others, so adding a
//   kind on the API side never breaks or silently drops anything); a blank
//   or duplicate kind token is dropped;
// - a token the FE does not know is DROPPED (the API validates tokens, so
//   an unknown one would be a 400 on the way back);
// - a duplicate token keeps its first role (to wins over cc);
// - extra rows without a usable e-mail are dropped; an unknown role = "to".

const asRecord = (raw: unknown): Record<string, unknown> =>
  raw !== null && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};

function kindToken(value: unknown): EmailRoutingKind | null {
  if (typeof value !== "string") return null;
  const token = value.trim();
  return token === "" ? null : token;
}

function tokens(raw: unknown, exclude: Set<string> = new Set()): EmailRoutingToken[] {
  if (!Array.isArray(raw)) return [];
  const out: EmailRoutingToken[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const token = item.trim();
    if (!(EMAIL_ROUTING_TOKENS as readonly string[]).includes(token)) continue;
    if (exclude.has(token) || out.includes(token as EmailRoutingToken)) continue;
    out.push(token as EmailRoutingToken);
  }
  return out;
}

function extras(raw: unknown): EmailRoutingExtra[] {
  if (!Array.isArray(raw)) return [];
  const out: EmailRoutingExtra[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const r = asRecord(item);
    const email = typeof r.email === "string" ? r.email.trim() : "";
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      email,
      role: typeof r.role === "string" && r.role.toLowerCase() === "cc" ? "cc" : "to",
    });
  }
  return out;
}

export function normalizeEmailRoutingRule(raw: unknown): EmailRoutingRule {
  const r = asRecord(raw);
  const to = tokens(r.to);
  return { to, cc: tokens(r.cc, new Set(to)), extra: extras(r.extra) };
}

export function mapVendorEmailSettings(raw: unknown): VendorEmailSettings {
  const r = asRecord(raw);
  const settings: EmailRoutingRules = {};
  const defaults: VendorEmailSettings["defaults"] = {};
  let allowsExtraRecipients: VendorEmailSettings["allowsExtraRecipients"];
  let allowedTokens: VendorEmailSettings["allowedTokens"];
  const kinds = Array.isArray(r.kinds) ? r.kinds : [];
  const infos: EmailRoutingKindInfo[] = [];
  for (const item of kinds) {
    const k = asRecord(item);
    const kind = kindToken(k.kind);
    if (kind === null || infos.some((i) => i.kind === kind)) continue;
    infos.push({
      kind,
      name: typeof k.name === "string" && k.name.trim() !== "" ? k.name.trim() : null,
      requiresTo: k.requiresTo === true,
      preferenceKey:
        typeof k.preferenceKey === "string" && k.preferenceKey.trim() !== ""
          ? k.preferenceKey.trim()
          : null,
      help: typeof k.help === "string" && k.help.trim() !== "" ? k.help.trim() : null,
    });
    if (k.rule !== null && k.rule !== undefined && typeof k.rule === "object") {
      settings[kind] = normalizeEmailRoutingRule(k.rule);
    }
    // defaultTo is a chain when it has more than one token (see
    // EmailRoutingDefault); the API never sends a default CC.
    defaults[kind] = { to: tokens(k.defaultTo), cc: [] };
    if (typeof k.allowsExtraRecipients === "boolean") {
      allowsExtraRecipients = { ...(allowsExtraRecipients ?? {}), [kind]: k.allowsExtraRecipients };
    }
    if (Array.isArray(k.allowedTokens)) {
      allowedTokens = { ...(allowedTokens ?? {}), [kind]: tokens(k.allowedTokens) };
    }
  }
  const candidates: VendorEmailSettings["candidates"] = {};
  for (const [token, value] of Object.entries(asRecord(r.candidates))) {
    if (!(EMAIL_ROUTING_TOKENS as readonly string[]).includes(token)) continue;
    candidates[token as EmailRoutingToken] =
      typeof value === "string" && value.trim() !== "" ? value.trim() : null;
  }
  return {
    canEdit: typeof r.canEdit === "boolean" ? r.canEdit : undefined,
    kinds: infos.length > 0 ? infos : undefined,
    settings,
    defaults,
    candidates,
    allowsExtraRecipients,
    allowedTokens,
  };
}

/** True when the PUT answered with the settings DTO (its `kinds` list), not "" / an older API. */
export function isVendorEmailSettingsAnswer(raw: unknown): boolean {
  return Array.isArray(asRecord(raw).kinds);
}

/**
 * The PUT body: every routable kind - present = its rule, absent = null
 * (the API resets that kind to today's recipients; a kind LEFT OUT would
 * keep its stored row, so every kind is always named = idempotent). The
 * kinds named are the ones the API LISTED in its GET (`kinds`, in its
 * order) - the five known kinds when the API sent no list - plus any kind
 * the draft has a rule for. The caller passes the WHOLE draft (the stored
 * rules of the untouched kinds included) - the view-model does, since its
 * draft is a clone of the server state, so an untouched kind is sent back
 * unchanged. E-mails are trimmed and lower-cased (the API dedupes
 * case-insensitively).
 */
export function toPutVendorEmailSettingsBody(
  rules: EmailRoutingRules,
  kinds: readonly EmailRoutingKind[] = EMAIL_ROUTING_KINDS,
): PutVendorEmailSettingsBody {
  const settings: PutVendorEmailSettingsBody["settings"] = {};
  const named = [...kinds, ...Object.keys(rules).filter((k) => !kinds.includes(k))];
  for (const kind of named) {
    const rule = rules[kind];
    settings[kind] = rule
      ? {
          to: [...rule.to],
          cc: [...rule.cc],
          extra: rule.extra.map((e) => ({ email: e.email.trim().toLowerCase(), role: e.role })),
        }
      : null;
  }
  return { settings };
}
